<script>
  import { onMount } from 'svelte';
  import { Link } from 'svelte-routing';
  import { api } from '../lib/api.js';
  import TextField from '../components/TextField.svelte';
  import { ui } from '../lib/ui.svelte.js';

  let buckets = $state([]);
  let loading = $state(true);
  let err = $state('');
  let creating = $state(false);
  let editingId = $state(null); // bucket id being edited, or null = create
  let newName = $state('');
  let newSlug = $state('');
  let newDescription = $state('');

  // Mirrors services.SlugifyName so the form preview matches what the
  // server will produce. Anything outside [a-z0-9] becomes "-", runs
  // collapse, leading/trailing hyphens are trimmed.
  function slugifyName(s) {
    return s.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Live preview: explicit slug wins, then a name-derived slug, then a
  // placeholder hinting that a random hash will be assigned.
  let previewUrl = $derived(
    window.location.origin + '/h/' +
    (newSlug.trim() || slugifyName(newName) || '<auto>')
  );

  async function load() {
    loading = true; err = '';
    try { buckets = (await api.listBuckets()).buckets || []; }
    catch (e) { err = e.message; }
    finally { loading = false; }
  }
  onMount(load);

  function resetForm() {
    newName = ''; newSlug = ''; newDescription = ''; creating = false; editingId = null;
  }
  function openCreate() {
    resetForm();
    creating = true;
  }
  function openEdit(b) {
    editingId = b.id; creating = false;
    newName = b.name || '';
    newSlug = b.id;            // current slug; changing it renames the bucket
    newDescription = b.description || '';
  }

  // Create a new bucket, or save edits to an existing one. On edit, sending
  // the (unchanged) slug is a no-op server-side; changing it triggers a rename
  // that repoints the bucket's captures.
  async function submit() {
    if (!newName.trim()) return;
    try {
      if (editingId) {
        await api.updateBucket(editingId, {
          name: newName.trim(),
          slug: newSlug.trim() || undefined,
          description: newDescription,
        });
        ui.snack('Bucket updated', 'success');
      } else {
        await api.createBucket({
          name: newName,
          slug: newSlug.trim() || undefined,
          description: newDescription,
        });
        ui.snack('Bucket created', 'success');
      }
      resetForm();
      load();
    } catch (e) { ui.snack(e.message, 'error'); }
  }

  function fmtTime(t) { return t ? new Date(t).toLocaleString() : '—'; }

  async function del(id) {
    const ok = await ui.ask({
      title: 'Delete bucket?',
      message: 'The bucket and all of its captures will be permanently removed.',
      confirmText: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try { await api.deleteBucket(id); ui.snack('Bucket deleted', 'success'); load(); }
    catch (e) { ui.snack(e.message, 'error'); }
  }

  function urlFor(id) {
    return window.location.origin + '/h/' + id;
  }

  function copy(text) {
    navigator.clipboard.writeText(text);
  }
</script>

<div class="flex justify-end mb-4">
  <button onclick={openCreate} class="shrink-0 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary text-on-primary text-sm font-medium px-3 sm:px-5 py-2.5 sm:py-2" title="New bucket">
    <span class="material-symbols-outlined text-base">add</span>
    <span class="hidden sm:inline">New bucket</span>
  </button>
</div>

{#if creating || editingId}
  <section class="bg-surface-container rounded-2xl p-4 mb-4 bucket-form">
    <h2 class="text-lg font-medium mb-3">{editingId ? 'Edit bucket' : 'New bucket'}</h2>
    <div class="bucket-form-grid">
      <TextField label="Name" bind:value={newName} />
      <TextField label={editingId ? 'URL slug' : 'URL slug (optional)'} bind:value={newSlug} leadingIcon="link" />
      <div class="bucket-form-wide">
        <TextField label="Description (optional)" bind:value={newDescription} />
      </div>
    </div>
    <p class="bucket-form-hint">
      <span class="material-symbols-outlined text-base align-middle">info</span>
      {#if editingId}
        Changing the slug renames the bucket and repoints all of its captures to the new URL <code>{previewUrl}</code>.
      {:else}
        Lowercase letters, digits and hyphens (3–64 chars). Leave blank to derive
        from the name (a random suffix is added if that name is already taken).
        Webhook URL will be <code>{previewUrl}</code>.
      {/if}
    </p>
    <div class="bucket-form-actions">
      <button onclick={resetForm} class="px-4 py-2 rounded-full text-sm text-on-surface-variant hover:bg-surface-container-high">Cancel</button>
      <button onclick={submit} class="px-6 py-2.5 rounded-full bg-primary text-on-primary text-sm font-medium">{editingId ? 'Save' : 'Create'}</button>
    </div>
  </section>
{/if}

{#if loading}
  <p class="text-on-surface-variant">Loading...</p>
{:else if err}
  <p class="text-error">{err}</p>
{:else if buckets.length === 0}
  <div class="bg-surface-container rounded-2xl p-12 text-center text-on-surface-variant">
    <span class="material-symbols-outlined text-5xl mb-2">folder_open</span>
    <p>No buckets yet. Create one to get a scoped webhook URL you can share.</p>
  </div>
{:else}
  <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
    {#each buckets as b (b.id)}
      <article class="bg-surface-container rounded-2xl p-4 flex flex-col">
        <header class="flex items-start justify-between mb-2 gap-2">
          <Link to={`/buckets/${b.id}`} class="font-medium text-lg hover:underline min-w-0 truncate">{b.name}</Link>
          <div class="flex items-center shrink-0">
            <button onclick={() => openEdit(b)} class="p-1 text-on-surface-variant hover:text-primary" title="Edit">
              <span class="material-symbols-outlined text-base">edit</span>
            </button>
            <button onclick={() => del(b.id)} class="p-1 text-on-surface-variant hover:text-error" title="Delete">
              <span class="material-symbols-outlined text-base">delete</span>
            </button>
          </div>
        </header>
        {#if b.description}<p class="text-sm text-on-surface-variant mb-3">{b.description}</p>{/if}
        <dl class="bucket-meta">
          <div><dt>Captures</dt><dd>{(b.capture_count ?? 0).toLocaleString()}</dd></div>
          <div><dt>Created</dt><dd>{fmtTime(b.created_at)}</dd></div>
          {#if b.owner_email}<div class="bucket-meta-wide"><dt>Owner</dt><dd class="truncate" title={b.owner_email}>{b.owner_email}</dd></div>{/if}
        </dl>
        <div class="mt-auto flex items-center gap-2 pt-3">
          <code class="flex-1 font-mono text-xs bg-surface px-3 py-2 rounded-lg truncate" title={urlFor(b.id)}>{urlFor(b.id)}</code>
          <button onclick={() => copy(urlFor(b.id))} class="p-2 rounded-full hover:bg-surface-container-high" title="Copy URL">
            <span class="material-symbols-outlined text-base">content_copy</span>
          </button>
        </div>
      </article>
    {/each}
  </div>
{/if}

<style>
  .bucket-form-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 0.75rem;
  }
  .bucket-form-wide { grid-column: 1 / -1; }
  .bucket-form-hint {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-block-start: 0.75rem;
    font-size: 12px;
    color: rgb(var(--md-sys-color-on-surface-variant));
  }
  .bucket-form-hint code {
    font-family: 'Menlo', 'Consolas', monospace;
    background: rgb(var(--md-sys-color-on-surface) / 0.06);
    padding: 1px 6px;
    border-radius: 4px;
  }
  .bucket-form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-block-start: 1rem;
  }
  /* Per-card metadata: count + created date side by side, owner full-width. */
  .bucket-meta {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.25rem 1rem;
    margin: 0;
    font-size: 12px;
  }
  .bucket-meta-wide { grid-column: 1 / -1; }
  .bucket-meta div { display: flex; justify-content: space-between; gap: 0.5rem; min-inline-size: 0; }
  .bucket-meta dt { color: rgb(var(--md-sys-color-on-surface-variant)); }
  .bucket-meta dd { margin: 0; font-variant-numeric: tabular-nums; min-inline-size: 0; }
</style>
